import React from 'react';
import ParticlesTheme from './ParticlesTheme';
import RainTheme from './RainTheme';
import AuroraTheme from './AuroraTheme';
import StarfieldTheme from './StarfieldTheme';
import BlobsTheme from './BlobsTheme';
import MatrixTheme from './MatrixTheme';
import ComplianceTheme from './ComplianceTheme';
import MinimalTheme from './MinimalTheme';
import BlueprintTheme from './BlueprintTheme';
import FirefliesTheme from './FirefliesTheme';
import BokehTheme from './BokehTheme';
import SynthwaveTheme from './SynthwaveTheme';
import WavesTheme from './WavesTheme';

// 3D scenes are lazy so three.js stays out of the main bundle.
const CloudsTheme = React.lazy(() => import('./three/CloudsTheme'));
const FogTheme = React.lazy(() => import('./three/FogTheme'));
const GalaxyTheme = React.lazy(() => import('./three/GalaxyTheme'));
const EarthTheme = React.lazy(() => import('./three/EarthTheme'));
const SolarSystemTheme = React.lazy(() => import('./three/SolarSystemTheme'));

export const THEME_CATEGORIES = ['3D Scenes', 'Nature & Sky', 'Ambient', 'Stylized'];

export const THEME_REGISTRY = {
  // 3D Scenes
  clouds:      { label: 'Clouds',              component: CloudsTheme,      category: '3D Scenes', threeD: true, description: 'Drifting through a volumetric cloud layer' },
  fog:         { label: 'Fog',                 component: FogTheme,         category: '3D Scenes', threeD: true, description: 'Smoky fog drifting past with glowing light inside' },
  galaxy:      { label: 'Spiral Galaxy',       component: GalaxyTheme,      category: '3D Scenes', threeD: true, description: 'A slowly rotating particle galaxy with a glowing core' },
  earth:       { label: 'Earth Globe',         component: EarthTheme,       category: '3D Scenes', threeD: true, description: 'A photoreal day/night Earth with drifting clouds and an atmospheric glow' },
  solarSystem: { label: 'Solar System',        component: SolarSystemTheme, category: '3D Scenes', threeD: true, description: 'Planets drifting around a glowing sun' },
  // Nature & Sky
  rain:        { label: 'Rain',                component: RainTheme,        category: 'Nature & Sky', description: 'Falling rain with ripples' },
  aurora:      { label: 'Aurora',              component: AuroraTheme,      category: 'Nature & Sky', description: 'Flowing northern lights ribbons' },
  starfield:   { label: 'Starfield',           component: StarfieldTheme,   category: 'Nature & Sky', description: 'Warp-speed starfield' },
  fireflies:   { label: 'Fireflies',           component: FirefliesTheme,   category: 'Nature & Sky', description: 'Warm drifting glowing dots on a dusk meadow' },
  waves:       { label: 'Waves',               component: WavesTheme,       category: 'Nature & Sky', description: 'Layered flowing ribbons of palette-colored current' },
  // Ambient
  particles:   { label: 'Particles',           component: ParticlesTheme,   category: 'Ambient', description: 'Connected network of drifting points' },
  blobs:       { label: 'Gradient Blobs',      component: BlobsTheme,       category: 'Ambient', description: 'Soft drifting gradient orbs' },
  bokeh:       { label: 'Bokeh',               component: BokehTheme,       category: 'Ambient', description: 'Cinematic soft-focus light orbs with depth' },
  minimal:     { label: 'Minimal',             component: MinimalTheme,     category: 'Ambient', description: 'Just a subtle glow' },
  // Stylized
  matrix:      { label: 'Matrix Rain',         component: MatrixTheme,      category: 'Stylized', description: 'Cascading digital code' },
  blueprint:   { label: 'Blueprint',           component: BlueprintTheme,   category: 'Stylized', description: 'Architectural floor plans drawing themselves on a drafting grid' },
  synthwave:   { label: 'Golden Hour',         component: SynthwaveTheme,   category: 'Nature & Sky', description: 'A real sunset photo brought to life with god rays and drifting light' },
  compliance:  { label: 'Neighborhood Skyline', component: ComplianceTheme, category: 'Stylized', description: 'Real-estate parallax: houses, townhomes, apartments, and downtown high-rises' },
};

export const THEME_KEYS = Object.keys(THEME_REGISTRY);

// Pseudo-theme: picks a random real theme each time the screensaver activates.
export const SHUFFLE_KEY = 'shuffle';

export function pickRandomThemeKey() {
  return THEME_KEYS[Math.floor(Math.random() * THEME_KEYS.length)];
}

export function getTheme(key) {
  return THEME_REGISTRY[key] || THEME_REGISTRY.particles;
}
